import { formatLongDateTimeString } from '@bymason/utils';
import {
  DeviceDataUsage,
  DataUsageCycle,
  SimType,
  SimState,
  UpdateSimStatusSubet,
} from '@bymason/controller-api-hooks';

const DAY_IN_MILLIS = 86400000;
const MASON_SYSTEM_ALIAS = 'Mason system applications';
export const DEVICE_DATA_ID = 'device';
export const TOTALS_ID = 'totals';

export enum UsageTimeframe {
  PreviousMonth,
  PreviousDay,
}
const timeframeInDaysMap = {
  [UsageTimeframe.PreviousMonth]: 30,
  [UsageTimeframe.PreviousDay]: 1,
};

interface UtilizationRow {
  id: string | number;
  package: string;
  wifi: number;
  mobile: number;
}

interface UtiliaztionMap {
  apps: UtilizationRow[] | null;
  totals: UtilizationRow | null;
}

const getUsageDataFromUsageCycles = (cycles: DataUsageCycle[], timeframe: UsageTimeframe) => {
  const cycle = cycles?.find((c) => {
    if (c.endTime && c.startTime) {
      const endDate = new Date(c.endTime).getTime();
      const startDate = new Date(c.startTime).getTime();
      const days = Math.ceil(Math.abs((endDate - startDate) / DAY_IN_MILLIS));
      return days === timeframeInDaysMap[timeframe];
    }
  });
  // can use index instead, if cycles is a tuple of [MonthUsage, DayUsage]
  // but this needs to be verified
  // const cycle = cycles?.[timeframe];

  return (cycle?.usages || []).map((usage) => {
    const { bytesSent, bytesReceived, type } = usage;

    return {
      type,
      bytesSent: bytesSent || 0,
      bytesReceived: bytesReceived || 0,
      bytesTotal: (bytesSent || 0) + (bytesReceived || 0),
    };
  });
};

export function getUsageDataGridRows(
  dataUsage: DeviceDataUsage,
  timeframe: UsageTimeframe = UsageTimeframe.PreviousMonth
): UtiliaztionMap {
  const appsRows: UtilizationRow[] | null =
    dataUsage.appsDataUsage
      ?.filter((appData) => {
        return typeof appData.uid === 'number' && appData.usageCycles?.[timeframe];
      })
      ?.map((appData) => {
        // asserting as non-null because of filter above
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const appUsageData = getUsageDataFromUsageCycles(appData.usageCycles!, timeframe);

        return {
          // asserting as non-null because of filter above
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          id: appData.uid!,
          package: appData.uid === 1000 ? MASON_SYSTEM_ALIAS : appData.packageNames?.join(', ') || '',
          wifi: appUsageData.find((u) => u.type === 'wifi')?.bytesTotal || 0,
          mobile: appUsageData.find((u) => u.type === 'mobile')?.bytesTotal || 0,
        };
      }) || null;

  const deviceUsageData = dataUsage.usageCycles
    ? getUsageDataFromUsageCycles(dataUsage.usageCycles, timeframe)
    : null;
  const deviceRow: UtilizationRow | null = deviceUsageData
    ? {
        id: DEVICE_DATA_ID,
        package: 'Device Data',
        wifi: deviceUsageData.find((u) => u.type === 'wifi')?.bytesTotal || 0,
        mobile: deviceUsageData.find((u) => u.type === 'mobile')?.bytesTotal || 0,
      }
    : null;

  let totalsRow = null;
  if (deviceRow) {
    // total is device usage
    totalsRow = { ...deviceRow, id: TOTALS_ID, package: 'Total' };
  }

  return { apps: appsRows, totals: totalsRow };
}

export function getUsageDataDateRange(
  dataUsage: DeviceDataUsage,
  timeframe: UsageTimeframe = UsageTimeframe.PreviousMonth
) {
  const startDate = dataUsage.usageCycles?.[timeframe]?.startTime;
  const endDate = dataUsage.usageCycles?.[timeframe]?.endTime;

  if (startDate && endDate) {
    return `${formatLongDateTimeString(startDate)} to ${formatLongDateTimeString(endDate)}`;
  }
}

export const isSimEnabled = (simType: SimType, status: SimState) => {
  if (simType === 'mock' || simType === 'twilio_super') {
    return status !== 'inactive';
  } else {
    return status !== 'suspended' && status !== 'deactivated' && status !== 'canceled';
  }
};

export const isSimPending = (status: SimState) => {
  return status === 'updating' || status === 'scheduled';
};

export function getSimEnabledDisabledStatus(simType: SimType, enabled: boolean): UpdateSimStatusSubet {
  if (simType === 'mock' || simType === 'twilio_super') {
    return enabled ? 'active' : 'inactive';
  } else {
    return enabled ? 'active' : 'suspended';
  }
}
